import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import Page from '../components/global/page';
import Violin from '../components/plots/violin';
import CourseDist from '../components/plots/courseDist';
import { getCourseDepartment } from '../util/functions';

const Selection = styled.select`
  margin-left: 10px;
  margin-right: 30px;
  height: 40px;
`;



const SelectionGroup = styled.div`
  display: flex;
  margin: 5px 0;
  align-items: center;
`;
const SelectionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const PlotCenter = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Courses = ({ data }) => {
  const [dept, setDeptCourse] = useState('');
  const [courses, setCourses] = useState([]);
  const [course, setCourseName] = useState('');
  const [courseData, setCourseData] = useState([]);

  const setDept = (dept) => {
    setDeptCourse(dept);
    const nextCourses = [
      ...new Set(
        data.allCourseGradesCsv.nodes
          .filter((d) => getCourseDepartment(d.course.replace(/\s+/g,' ')) === dept)
          .map((d) => d.course.replace(/\s+/g,' ')),
      ),
    ].sort((a, b) => {
      const [, aarea, anum, aletter] = a.match(/ ([A-VX-Z ]*)(\d+)(\w+)?$/);
      const [, barea, bnum, bletter] = b.match(/ ([A-VX-Z ]*)(\d+)(\w+)?$/);
      if (+anum === +bnum) {
        return aletter < bletter ? -1 : 1;
      }
      return +anum - +bnum;
    });

    setCourses(nextCourses);
    setCourse(nextCourses[0].replace(/\s+/g,' '));
  };

  const setCourse = (c) => {
    const newCData = data.allCourseGradesCsv.nodes.filter(
      (d) => d.course.replace(/\s+/g,' ') === c,
    );

    if (newCData) {
      setCourseName(c);
      setCourseData(newCData);
    }
  };

  const depts = [
    ...new Set(data.allCourseGradesCsv.nodes.map((d) => getCourseDepartment(d.course.replace(/\s+/g,' ')))),
  ].sort((a, b) => (a < b ? -1 : 1));

  useEffect(() => {
    setDept('ANTH');
    setCourse('ANTH 2');
  }, []);

  if (typeof window === 'undefined') {
    return <></>;
  }
  return (
    <Page>
      <h1>Course Search</h1>
      <p>
        This page shows grade distributions for a particular course and how it
        has changed over time and with different instructors.
      </p>
      <SelectionContainer>
        <SelectionGroup>
          <p>Select a department:</p>
          <Selection
            onInputCapture={(d) => {
              setDept(d.target.value);
            }}
          >
            {depts.map((d) => (
              <option value={d} key={d} selected={d === dept}>
                {d}
              </option>
            ))}
          </Selection>
        </SelectionGroup>
        <SelectionGroup>
          <p>Select a course:</p>
          <Selection
            onInputCapture={(d) => {
              console.log(d.target.value);
              setCourse(d.target.value);
            }}
          >
            {courses.map((d) => (
              <option value={d.replace(/\s+/g,' ')} key={d} selected={d === course}>
                {d}
              </option>
            ))}
          </Selection>
        </SelectionGroup>
      </SelectionContainer>
      <p>
        For every time this course was offered, the average GPA, passing
        percentage or percentage of satisfactory grades were calculated, where
        applicable. These averages and percentages are shown as points in the
        plot below.
      </p>
      <p>
        For courses offered multiple times since Fall Quarter 2009, the
        distribution of these points is also shown, which, not accounting for
        different instructors or quarters, shows how the average student
        performs in this course.
      </p>
      {course ? (
        <PlotCenter>
          <Violin
            data={[{ key: course, values: courseData }]}
            halfSize={false}
            link={false}
            width={Math.min(600, window.innerWidth - 40)}
          />
        </PlotCenter>
      ) : null}
      <p>
        Many of these distributions are either stretched out or have multiple
        peaks. This is often due to differences in professors or also grading
        changes during the COVID-19 pandemic.
      </p>
      <p>
        The entire grading history for this course is shown below. Tap or move
        the mouse over these distributions for a more detailed breakdown.
      </p>
      {course ? (
        <>
          <h3>
            Grading History for&nbsp;
            {course}
          </h3>
          <PlotCenter>
            <CourseDist data={courseData} />
          </PlotCenter>
        </>
      ) : null}
    </Page>
  );
};

export const query = graphql`
  query MyQuery1 {
    allCourseGradesCsv {
      nodes {
        A
        Am
        Ap
        B
        Bm
        C
        Bp
        Cm
        Cp
        D
        Dm
        Dp
        F
        P
        S
        su
        avgGPA
        course
        dept
        instructor
        nLetterStudents
        nPNPStudents
        quarter
        year
      }
    }
  }
`;

export default Courses;

